import React, { useState } from "react";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogDescription,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { PlusIcon, XMarkIcon } from "@heroicons/react/16/solid";
import { Input } from "../../../design/input";
import { Textarea } from "../../../design/textarea";
import { Text } from "../../../design/text";
import { Select } from "../../../design/select";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../../../design/fieldset";
import { useCase } from "../../../contexts/CaseContext";
import { useAttorney } from "../../../contexts/AttorneyContext";
import { useAttorneyDialog } from "../../../providers/AttorneyDialogProvider";
import { useNavigate } from "react-router-dom";
import {
  ProgramType,
  PROGRAM_TYPE_LABELS,
  ServiceType,
  SERVICE_TYPE_LABELS,
  PRIMARY_SERVICE_TYPES,
  AttorneyRole,
  ATTORNEY_ROLE_LABELS,
  getAttorneyDisplayName,
  SERVICE_TYPE_PREFIXES,
  DEFAULT_CASE_PREFIX,
} from "@visitproof/types";
import {
  CheckCircleIcon,
  DocumentTextIcon,
  FolderIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  ScaleIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";

// Attorney assignment for the case
interface AttorneyAssignment {
  attorneyId: string;
  role: AttorneyRole;
}

interface FormData {
  identifier: string; // Optional - if empty, will be auto-generated
  useCustomIdentifier: boolean; // Whether user wants to use a custom identifier
  notes: string;
  programTypes: ProgramType[];
  serviceTypes: ServiceType[];
  attorneyAssignments: AttorneyAssignment[];
}

interface FormErrors {
  identifier?: string;
  general?: string;
  attorneys?: string;
}

/**
 * Get the prefix for a service type array.
 * Uses the first service type if multiple are selected.
 */
function getPreviewPrefix(serviceTypes: ServiceType[]): string {
  if (serviceTypes.length === 0) {
    return DEFAULT_CASE_PREFIX || "CS";
  }
  return SERVICE_TYPE_PREFIXES?.[serviceTypes[0]] || DEFAULT_CASE_PREFIX || "CS";
}

/**
 * Generate a preview of what the case identifier will look like.
 * Note: The actual sequence number will be assigned on creation.
 */
function getIdentifierPreview(serviceTypes: ServiceType[]): string {
  const prefix = getPreviewPrefix(serviceTypes);
  const year = new Date().getFullYear();
  return `${prefix}-${year}-XXX`;
}

type Step = 1 | 2 | 3 | 4;

// Step indicator component
const StepIndicator: React.FC<{
  currentStep: Step;
  totalSteps: number;
  stepLabels: string[];
}> = ({ currentStep, totalSteps, stepLabels }) => (
  <div className="mb-6">
    <div className="flex items-center justify-between">
      {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
        <React.Fragment key={step}>
          <div className="flex flex-col items-center">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors ${
                step < currentStep
                  ? "bg-green-500 text-white"
                  : step === currentStep
                    ? "bg-blue-500 text-white"
                    : "bg-zinc-200 text-zinc-500 dark:bg-zinc-700 dark:text-zinc-400"
              }`}
            >
              {step < currentStep ? (
                <CheckCircleIcon className="h-5 w-5" />
              ) : (
                step
              )}
            </div>
            <span
              className={`mt-1 text-xs ${
                step === currentStep
                  ? "font-medium text-zinc-900 dark:text-white"
                  : "text-zinc-500 dark:text-zinc-400"
              }`}
            >
              {stepLabels[step - 1]}
            </span>
          </div>
          {step < totalSteps && (
            <div
              className={`mx-2 h-0.5 flex-1 ${
                step < currentStep
                  ? "bg-green-500"
                  : "bg-zinc-200 dark:bg-zinc-700"
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  </div>
);

// Section header component
const SectionHeader: React.FC<{
  icon: React.ElementType;
  title: string;
  description?: string;
}> = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-3 mb-4">
    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/20">
      <Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
    </div>
    <div>
      <Text className="font-semibold text-zinc-900 dark:text-zinc-100">
        {title}
      </Text>
      {description && (
        <Text className="text-sm text-zinc-500 dark:text-zinc-400">
          {description}
        </Text>
      )}
    </div>
  </div>
);

const CreateCaseButton: React.FC = () => {
  const { createCase } = useCase();
  const { attorneys, assignAttorneyToCase } = useAttorney();
  const { openCreateDialog } = useAttorneyDialog();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<Step>(1);
  const [formData, setFormData] = useState<FormData>({
    identifier: "",
    useCustomIdentifier: false,
    notes: "",
    programTypes: [],
    serviceTypes: [],
    attorneyAssignments: [],
  });
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  // Get active attorneys for selection
  const activeAttorneys = attorneys.filter((a) => a.status === "active");

  const validateStep = (currentStep: Step): boolean => {
    const newErrors: FormErrors = {};

    if (currentStep === 3) {
      // Validate attorney assignments - ensure all have an attorney selected
      for (const assignment of formData.attorneyAssignments) {
        if (!assignment.attorneyId) {
          newErrors.attorneys = "Please select an attorney for all assignments";
          break;
        }
      }
    }

    // Step 4 (Review) - validate custom identifier if user opted to use one
    if (currentStep === 4 && formData.useCustomIdentifier) {
      if (!formData.identifier.trim()) {
        newErrors.identifier = "Please enter a case identifier or use auto-generated";
      } else if (formData.identifier.trim().length < 2) {
        newErrors.identifier = "Case identifier must be at least 2 characters";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep((prev) => Math.min(prev + 1, 4) as Step);
    }
  };

  const handleBack = () => {
    setStep((prev) => Math.max(prev - 1, 1) as Step);
  };

  const handleSubmit = async () => {
    if (!validateStep(step)) {
      return;
    }

    setIsSaving(true);
    setErrors({});

    try {
      // Step 1: Create the case
      // If using custom identifier, pass it; otherwise pass empty string for auto-generation
      const caseId = await createCase({
        identifier: formData.useCustomIdentifier ? formData.identifier.trim() : "",
        notes: formData.notes.trim() || undefined,
        programTypes:
          formData.programTypes.length > 0 ? formData.programTypes : undefined,
        serviceTypes:
          formData.serviceTypes.length > 0 ? formData.serviceTypes : undefined,
      });

      // Step 2: Assign attorneys to the case
      for (const assignment of formData.attorneyAssignments) {
        await assignAttorneyToCase({
          caseId,
          attorneyId: assignment.attorneyId,
          role: assignment.role,
          isActive: true,
        });
      }

      navigate(`/case/${caseId}`);
      clearForm();
    } catch (error) {
      console.error("Error creating case:", error);
      setErrors({
        general:
          error instanceof Error
            ? error.message
            : "Failed to create case. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange =
    (field: keyof FormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));

      // Clear field-specific error when user starts typing
      if (field === "identifier" && errors.identifier) {
        setErrors((prev) => ({
          ...prev,
          identifier: undefined,
        }));
      }
    };

  const clearForm = () => {
    setFormData({
      identifier: "",
      useCustomIdentifier: false,
      notes: "",
      programTypes: [],
      serviceTypes: [],
      attorneyAssignments: [],
    });
    setErrors({});
    setStep(1);
    setIsOpen(false);
  };

  const handleProgramTypeToggle = (programType: ProgramType) => {
    setFormData((prev) => ({
      ...prev,
      programTypes: prev.programTypes.includes(programType)
        ? prev.programTypes.filter((t) => t !== programType)
        : [...prev.programTypes, programType],
    }));
  };

  const handleServiceTypeToggle = (serviceType: ServiceType) => {
    setFormData((prev) => ({
      ...prev,
      serviceTypes: prev.serviceTypes.includes(serviceType)
        ? prev.serviceTypes.filter((t) => t !== serviceType)
        : [...prev.serviceTypes, serviceType],
    }));
  };

  // Attorney assignment handlers
  const handleAddAttorneyAssignment = () => {
    setFormData((prev) => ({
      ...prev,
      attorneyAssignments: [
        ...prev.attorneyAssignments,
        {
          attorneyId: "",
          role: "visiting_parent_attorney" as AttorneyRole,
        },
      ],
    }));
  };

  const handleAddNewAttorney = () => {
    openCreateDialog({
      onAttorneyCreated: (attorneyId) => {
        // Add the newly created attorney as an assignment
        setFormData((prev) => ({
          ...prev,
          attorneyAssignments: [
            ...prev.attorneyAssignments,
            {
              attorneyId,
              role: "visiting_parent_attorney" as AttorneyRole,
            },
          ],
        }));
        // Clear any attorney errors
        if (errors.attorneys) {
          setErrors((prev) => ({ ...prev, attorneys: undefined }));
        }
      },
    });
  };

  const handleRemoveAttorney = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      attorneyAssignments: prev.attorneyAssignments.filter(
        (_, i) => i !== index
      ),
    }));
  };

  const handleAttorneyChange = (
    index: number,
    field: keyof AttorneyAssignment,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      attorneyAssignments: prev.attorneyAssignments.map((a, i) =>
        i === index ? { ...a, [field]: value } : a
      ),
    }));
    // Clear any attorney errors
    if (errors.attorneys) {
      setErrors((prev) => ({ ...prev, attorneys: undefined }));
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      clearForm();
    }
  };

  // Helper to get attorney display info
  const getAttorneyInfo = (assignment: AttorneyAssignment): string => {
    const attorney = attorneys.find((a) => a.id === assignment.attorneyId);
    return attorney ? getAttorneyDisplayName(attorney) : "Select attorney";
  };

  const stepLabels = ["Services", "Programs", "Attorneys", "Review"];

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        <PlusIcon className="h-4 w-4" />
        Create Case
      </Button>

      <Dialog open={isOpen} onClose={handleClose} size="2xl">
        <DialogTitle>Create New Case</DialogTitle>
        <DialogDescription>
          {step === 1 && "Select the service types for this case. This determines the case ID prefix."}
          {step === 2 && "Select the program types for this case."}
          {step === 3 && "Add attorneys to this case (optional)."}
          {step === 4 && "Review the case details and add any notes."}
        </DialogDescription>

        <DialogBody>
          <StepIndicator
            currentStep={step}
            totalSteps={4}
            stepLabels={stepLabels}
          />

          {errors.general && (
            <ErrorMessage className="mb-4">{errors.general}</ErrorMessage>
          )}

          {/* Step 1: Service Types */}
          {step === 1 && (
            <div className="space-y-6">
              <SectionHeader
                icon={ClipboardDocumentListIcon}
                title="Service Types"
                description="Select the service types for this case. This determines the case ID prefix."
              />

              <div>
                <Text className="mb-3 block text-sm text-zinc-600 dark:text-zinc-400">
                  Choose the primary service type(s). The first selected type determines
                  the case ID prefix (e.g., SV for Supervised Visitation, EX for Safe Exchange).
                </Text>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {PRIMARY_SERVICE_TYPES.map((value) => {
                    const label = SERVICE_TYPE_LABELS[value];
                    const isChecked = formData.serviceTypes.includes(value);
                    const prefix = SERVICE_TYPE_PREFIXES?.[value] || DEFAULT_CASE_PREFIX;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => handleServiceTypeToggle(value)}
                        disabled={isSaving}
                        className={`flex items-center gap-3 rounded-lg border-2 p-3 text-left transition-all ${
                          isChecked
                            ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20"
                            : "border-zinc-200 bg-white hover:border-zinc-300 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-zinc-600"
                        }`}
                      >
                        <div
                          className={`flex h-5 w-5 items-center justify-center rounded border-2 ${
                            isChecked
                              ? "border-blue-500 bg-blue-500"
                              : "border-zinc-300 dark:border-zinc-600"
                          }`}
                        >
                          {isChecked && (
                            <CheckCircleIcon className="h-4 w-4 text-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <span
                            className={`text-sm font-medium ${
                              isChecked
                                ? "text-blue-700 dark:text-blue-300"
                                : "text-zinc-700 dark:text-zinc-300"
                            }`}
                          >
                            {label}
                          </span>
                          <span
                            className={`ml-2 text-xs ${
                              isChecked
                                ? "text-blue-500 dark:text-blue-400"
                                : "text-zinc-400 dark:text-zinc-500"
                            }`}
                          >
                            ({prefix})
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Case ID Preview */}
                <div className="mt-4 rounded-lg border border-zinc-200 bg-zinc-50 p-3 dark:border-zinc-700 dark:bg-zinc-800/50">
                  <Text className="text-xs text-zinc-500 dark:text-zinc-400 mb-1">
                    Case ID Preview
                  </Text>
                  <Text className="font-mono text-sm font-medium text-zinc-900 dark:text-white">
                    {getIdentifierPreview(formData.serviceTypes)}
                  </Text>
                  <Text className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">
                    The actual sequence number will be assigned when the case is created.
                  </Text>
                </div>

                {formData.serviceTypes.length > 0 && (
                  <Text className="mt-3 text-sm text-zinc-600 dark:text-zinc-400">
                    {formData.serviceTypes.length} service type
                    {formData.serviceTypes.length !== 1 ? "s" : ""} selected
                  </Text>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Program Types */}
          {step === 2 && (
            <div className="space-y-6">
              <SectionHeader
                icon={Cog6ToothIcon}
                title="Program Types"
                description="Select the program types that apply to this case"
              />

              <div>
                <Text className="mb-3 block text-sm text-zinc-600 dark:text-zinc-400">
                  Choose all program types that apply. You can skip this step if
                  none apply.
                </Text>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {Object.entries(PROGRAM_TYPE_LABELS).map(([value, label]) => {
                    const isChecked = formData.programTypes.includes(
                      value as ProgramType
                    );
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() =>
                          handleProgramTypeToggle(value as ProgramType)
                        }
                        disabled={isSaving}
                        className={`flex items-center gap-3 rounded-lg border-2 p-3 text-left transition-all ${
                          isChecked
                            ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20"
                            : "border-zinc-200 bg-white hover:border-zinc-300 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-zinc-600"
                        }`}
                      >
                        <div
                          className={`flex h-5 w-5 items-center justify-center rounded border-2 ${
                            isChecked
                              ? "border-blue-500 bg-blue-500"
                              : "border-zinc-300 dark:border-zinc-600"
                          }`}
                        >
                          {isChecked && (
                            <CheckCircleIcon className="h-4 w-4 text-white" />
                          )}
                        </div>
                        <span
                          className={`text-sm font-medium ${
                            isChecked
                              ? "text-blue-700 dark:text-blue-300"
                              : "text-zinc-700 dark:text-zinc-300"
                          }`}
                        >
                          {label}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {formData.programTypes.length > 0 && (
                  <Text className="mt-3 text-sm text-zinc-600 dark:text-zinc-400">
                    {formData.programTypes.length} program type
                    {formData.programTypes.length !== 1 ? "s" : ""} selected
                  </Text>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Attorneys */}
          {step === 3 && (
            <div className="space-y-6">
              <SectionHeader
                icon={ScaleIcon}
                title="Legal Representatives"
                description="Add attorneys and other legal representatives to this case"
              />

              {errors.attorneys && (
                <ErrorMessage className="mb-4">{errors.attorneys}</ErrorMessage>
              )}

              <div>
                <Text className="mb-3 block text-sm text-zinc-600 dark:text-zinc-400">
                  Add attorneys who will be involved in this case. You can
                  select from existing attorneys or create new ones. This step
                  is optional.
                </Text>

                {/* Attorney assignments list */}
                <div className="space-y-4">
                  {formData.attorneyAssignments.map((assignment, index) => (
                    <div
                      key={assignment.attorneyId || index}
                      className="rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-3">
                          {/* Attorney selection */}
                          <Field>
                            <Label className="text-xs">Select Attorney</Label>
                            <Select
                              value={assignment.attorneyId}
                              onChange={(e) =>
                                handleAttorneyChange(
                                  index,
                                  "attorneyId",
                                  e.target.value
                                )
                              }
                            >
                              <option value="">Select an attorney...</option>
                              {activeAttorneys.map((attorney) => (
                                <option key={attorney.id} value={attorney.id}>
                                  {getAttorneyDisplayName(attorney)}
                                  {attorney.firmName &&
                                    ` - ${attorney.firmName}`}
                                </option>
                              ))}
                            </Select>
                          </Field>

                          {/* Role selection */}
                          <Field>
                            <Label className="text-xs">Role on Case</Label>
                            <Select
                              value={assignment.role}
                              onChange={(e) =>
                                handleAttorneyChange(
                                  index,
                                  "role",
                                  e.target.value
                                )
                              }
                            >
                              {Object.entries(ATTORNEY_ROLE_LABELS).map(
                                ([value, label]) => (
                                  <option key={value} value={value}>
                                    {label}
                                  </option>
                                )
                              )}
                            </Select>
                          </Field>
                        </div>

                        {/* Remove button */}
                        <button
                          type="button"
                          onClick={() => handleRemoveAttorney(index)}
                          className="mt-1 rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600 dark:hover:bg-zinc-800 dark:hover:text-zinc-300"
                          title="Remove attorney"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add attorney buttons */}
                <div className="mt-4 flex flex-wrap gap-2">
                  {activeAttorneys.length > 0 && (
                    <Button
                      type="button"
                      outline
                      onClick={handleAddAttorneyAssignment}
                    >
                      <ScaleIcon className="h-4 w-4" />
                      Select Existing Attorney
                    </Button>
                  )}
                  <Button type="button" outline onClick={handleAddNewAttorney}>
                    <UserPlusIcon className="h-4 w-4" />
                    Create New Attorney
                  </Button>
                </div>

                {formData.attorneyAssignments.length === 0 && (
                  <Text className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">
                    No attorneys added yet. You can add them now or later from
                    the case details page.
                  </Text>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Review & Notes */}
          {step === 4 && (
            <div className="space-y-6">
              <SectionHeader
                icon={DocumentTextIcon}
                title="Review & Confirm"
                description="Review the case details and add any notes"
              />

              {/* Case Identifier Section */}
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-700 dark:bg-zinc-800/50">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-zinc-900 dark:text-white">
                    Case Identifier
                  </h4>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      useCustomIdentifier: !prev.useCustomIdentifier,
                      identifier: prev.useCustomIdentifier ? "" : prev.identifier
                    }))}
                    className="text-xs text-blue-600 hover:text-blue-500 dark:text-blue-400"
                  >
                    {formData.useCustomIdentifier ? "Use auto-generated" : "Use custom identifier"}
                  </button>
                </div>

                {formData.useCustomIdentifier ? (
                  <Field>
                    <Input
                      id="custom-identifier"
                      type="text"
                      placeholder='e.g. "Case 2024-001" or "Smith v. Johnson"'
                      value={formData.identifier}
                      onChange={handleInputChange("identifier")}
                      disabled={isSaving}
                    />
                    {errors.identifier && (
                      <ErrorMessage>{errors.identifier}</ErrorMessage>
                    )}
                    <Text className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                      Enter a custom identifier for this case.
                    </Text>
                  </Field>
                ) : (
                  <div>
                    <Text className="font-mono text-lg font-medium text-zinc-900 dark:text-white">
                      {getIdentifierPreview(formData.serviceTypes)}
                    </Text>
                    <Text className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                      Will be auto-generated based on service type. The sequence number (XXX) will be assigned on creation.
                    </Text>
                  </div>
                )}
              </div>

              {/* Case Summary */}
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-700 dark:bg-zinc-800/50">
                <h4 className="mb-3 text-sm font-semibold text-zinc-900 dark:text-white">
                  Case Summary
                </h4>
                <dl className="space-y-3 text-sm">
                  <div>
                    <dt className="text-zinc-500 dark:text-zinc-400">
                      Service Types
                    </dt>
                    <dd className="font-medium text-zinc-900 dark:text-white">
                      {formData.serviceTypes.length > 0 ? (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {formData.serviceTypes.map((st) => (
                            <span
                              key={st}
                              className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300"
                            >
                              {SERVICE_TYPE_LABELS[st]}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-zinc-400 dark:text-zinc-500">
                          None selected (all will be available)
                        </span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-zinc-500 dark:text-zinc-400">
                      Program Types
                    </dt>
                    <dd className="font-medium text-zinc-900 dark:text-white">
                      {formData.programTypes.length > 0 ? (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {formData.programTypes.map((pt) => (
                            <span
                              key={pt}
                              className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                            >
                              {PROGRAM_TYPE_LABELS[pt]}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-zinc-400 dark:text-zinc-500">
                          None selected
                        </span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-zinc-500 dark:text-zinc-400">
                      Attorneys
                    </dt>
                    <dd className="font-medium text-zinc-900 dark:text-white">
                      {formData.attorneyAssignments.length > 0 ? (
                        <div className="mt-1 space-y-1">
                          {formData.attorneyAssignments.map(
                            (assignment, index) => (
                              <div
                                key={index}
                                className="flex items-center gap-2"
                              >
                                <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                  {ATTORNEY_ROLE_LABELS[assignment.role]}
                                </span>
                                <span className="text-zinc-700 dark:text-zinc-300">
                                  {getAttorneyInfo(assignment)}
                                </span>
                              </div>
                            )
                          )}
                        </div>
                      ) : (
                        <span className="text-zinc-400 dark:text-zinc-500">
                          None added
                        </span>
                      )}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Notes */}
              <Fieldset>
                <FieldGroup>
                  <Field>
                    <Label htmlFor="notes">Notes (Optional)</Label>
                    <Textarea
                      id="notes"
                      placeholder="Add any additional notes about this case..."
                      value={formData.notes}
                      onChange={handleInputChange("notes")}
                      disabled={isSaving}
                      rows={3}
                    />
                  </Field>
                </FieldGroup>
              </Fieldset>
            </div>
          )}
        </DialogBody>

        <DialogActions>
          {step > 1 ? (
            <Button plain onClick={handleBack} disabled={isSaving}>
              Back
            </Button>
          ) : (
            <Button plain onClick={handleClose} disabled={isSaving}>
              Cancel
            </Button>
          )}
          {step < 4 ? (
            <Button onClick={handleNext} disabled={isSaving}>
              Continue
            </Button>
          ) : (
            <Button
              disabled={isSaving || (formData.useCustomIdentifier && !formData.identifier.trim())}
              onClick={handleSubmit}
            >
              {isSaving ? "Creating..." : "Create Case"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateCaseButton;
